<template>
    <div class="tw:flex tw:flex-col tw:gap-4">
        <div v-if="canShowGeneratePodcast" class="tw:w-full podcast-config-panel">
            <podcast-configuration-summary
                class="tw:mb-3"
                :selected-count="selectedConfigCount"
                :readonly="!canEditPodcastConfiguration"
                @configure="isConfigDrawerOpen = true"
            />
            <e-field label="Delivery Instructions">
                <e-input
                    :model-value="generationContext"
                    type="textarea"
                    rows="4"
                    class="tw:w-full"
                    :disabled="!canEditPodcastConfiguration"
                    @update:modelValue="generationContext = $event"
                />
            </e-field>
            <e-field label="Script">
                <e-input
                    :model-value="script"
                    type="textarea"
                    rows="10"
                    class="tw:w-full podcast-script-editor"
                    :disabled="!canEditPodcastConfiguration"
                    @update:modelValue="handleScriptInput"
                />
            </e-field>
        </div>

        <podcast-configuration-drawer
            :open="isConfigDrawerOpen"
            :readonly="!canEditPodcastConfiguration"
            :model-value="podcastConfig"
            @update:open="isConfigDrawerOpen = $event"
            @update:modelValue="podcastConfig = $event"
            @apply="handleConfigApply"
        />

        <div v-if="canShowGeneratePodcast" class="tw:w-full">
            <e-field label="Podcast Generation">
                <div class="tw:mb-6">
                    <e-button type="is-primary" :loading="isGenerating" @click="generatePodcast">
                        {{ generationStatus === 'completed' ? 'Regenerate Podcast' : 'Generate Podcast' }}
                    </e-button>
                </div>
                <p class="tw:ml-4">
                    Status: <strong>{{ generationStatusLabel }}</strong>
                </p>
                <p v-if="generationError" class="tw:mt-1 tw:text-red-600">{{ generationError }}</p>
            </e-field>
        </div>

        <div v-if="!isActivityViewRoute && showInstructions" class="tw:w-full">
            <e-field>
                <display-content :content="activityContent.content" />
            </e-field>
        </div>

        <div v-if="showPendingOrGeneratingMessage" class="tw:w-full">
            <e-message
                type="is-warning"
                :closable="false"
            >
                This podcast is being generated and is not available yet. Please check back in a few minutes.
            </e-message>
        </div>

        <div v-if="generationStatus === 'failed'" class="tw:w-full">
            <e-message
                type="is-danger"
                :closable="false"
            >
                {{ failedGenerationMessage }}
            </e-message>
        </div>

        <div v-if="showCompletedWithoutAudioMessage" class="tw:w-full">
            <e-message
                type="is-info"
                :closable="false"
            >
                Podcast generation is complete, but the audio URL is not available yet. Please refresh in a moment.
            </e-message>
        </div>

        <div v-if="isPodcastReady" class="tw:w-full">
            <div class="podcast-player-wrap">
                <audio-player
                    :src="audioUrl"
                    :initial-position="lastPosition"
                    @timeupdate="onTimeUpdate"
                    @ended="onEnded"
                />
                <p class="tw:mt-2">
                    Listen progress: <strong>{{ listenPercentage }}%</strong>
                </p>
            </div>
        </div>

        <div v-if="isPodcastReady && transcriptScript" class="tw:w-full podcast-script-block">
            <e-collapse v-model:open="scriptAccordionOpen">
                <template #trigger="{ open, toggle }">
                    <div
                        class="podcast-script-trigger"
                        role="button"
                        tabindex="0"
                        @click.stop="toggle"
                        @keydown="onScriptTriggerKeydown($event, toggle)"
                    >
                        <span class="tw:font-medium tw:text-gray-800">Podcast transcript</span>
                        <i :class="open ? 'pi pi-chevron-up' : 'pi pi-chevron-down'" class="tw:text-gray-500 tw:transition-transform" aria-hidden="true" />
                    </div>
                </template>
                <div class="podcast-script-content">{{ transcriptScript }}</div>
            </e-collapse>
        </div>

        <PrimeVueDialog
            v-model:visible="showRegenerateScriptConfirmDialog"
            header="Regenerate podcast audio?"
            modal
            :style="{ width: '460px' }"
            append-to="body"
        >
            <div class="tw:pl-2">
                <p>
                    You changed the script. Regenerating will replace the current audio using the updated script.
                </p>
            </div>
            <template #footer>
                <PrimeVueButton label="Cancel" class="p-button-text" @click="showRegenerateScriptConfirmDialog = false" />
                <PrimeVueButton label="Regenerate with current script" class="p-button-primary" @click="queuePodcastGeneration" />
            </template>
        </PrimeVueDialog>

        <activity-completion-action-row class="tw:w-full">
            <e-button
                v-if="isPodcastReady && (userHasAuthorizedAttempt || (!completed && !readOnly))"
                :loading="isLoading"
                :disabled="disabled"
                type="is-primary"
                @click="submit"
            >
                {{ translate('mark_complete') }}
            </e-button>
        </activity-completion-action-row>

        <div v-if="!readOnly && canViewSubmissions" class="tw:w-full">
            <submissions :namespace="namespace" @fetch-submissions="toggleSubmissionLoading" />
        </div>
    </div>
</template>

<script>
import PrimeVueButton from 'primevue/button';
import PrimeVueDialog from 'primevue/dialog';
import { useI18n } from 'vue-i18n';
import { mapState } from 'vuex';
import ActivityCompletionActionRow from '@/components/activity-types/course-activity-types/ActivityCompletionActionRow.vue';
import Submissions from '@/components/activity-types/course-activity-types/activities/Submissions.vue';
import AudioPlayer from '@/components/audio/AudioPlayer.vue';
import DisplayContent from '@/components/editor/DisplayContent.vue';
import PodcastConfigurationDrawer from '@/components/podcast-configuration/PodcastConfigurationDrawer.vue';
import PodcastConfigurationSummary from '@/components/podcast-configuration/PodcastConfigurationSummary.vue';
import ECollapse from '@/components/ui/ECollapse.vue';
import AttemptAuthorizationMixin from '@/mixins/AttemptAuthorization.mixin.js';
import CourseActivityMixin from '@/mixins/CourseActivity.mixin.js';
import IsLateMixin from '@/mixins/IsLate.mixin.js';
import axios from '@/plugins/axios.js';

// Local-dev only: podcast generation needs a queue worker + a TTS provider that
// aren't available locally, so decks never resolve audio. These bundled placeholders
// let the player + transcript be exercised in dev. Stripped from production builds via
// the import.meta.env.DEV guard at the use site (see created()).
// Regenerate the audio with: php scripts/dev/generate-podcast-placeholder.php
const DEV_PLACEHOLDER_PODCAST_AUDIO = '/dev-audio/podcast-sample.wav';
const DEV_PLACEHOLDER_PODCAST_TRANSCRIPT = `The Bedside Manner Podcast
"Back to Basics — Nursing Fundamentals That Never Go Out of Style"

Hosts: Dr. Sandra Okafor, RN, DNP | James Whitfield, RN, BSN

[INTRO MUSIC FADES]

SANDRA: Welcome back to The Bedside Manner — the podcast where we talk about everything nursing, from the floors to the boardroom.

JAMES: Today we're going back to basics: nursing fundamentals. And this conversation is for every nurse, no matter how many years you have behind you.

(Dev placeholder transcript — real transcripts are produced during podcast generation.)`;

export default {
    name: 'CoursePodcast',

    components: {
        ActivityCompletionActionRow,
        AudioPlayer,
        DisplayContent,
        ECollapse,
        PrimeVueButton,
        PrimeVueDialog,
        PodcastConfigurationDrawer,
        PodcastConfigurationSummary,
        Submissions,
    },

    mixins: [IsLateMixin, CourseActivityMixin, AttemptAuthorizationMixin],

    setup() {
        const { t: translate } = useI18n();
        return { translate };
    },

    data: () => ({
        isConfigDrawerOpen: false,
        isGenerating: false,
        generationStatus: 'pending',
        generationError: null,
        generationStartedAt: null,
        generationCompletedAt: null,
        podcastScript: null,
        lastGeneratedScript: null,
        scriptAccordionOpen: false,
        audioUrl: null,
        listenPercentage: 0,
        lastPosition: 0,
        totalListenTimeSeconds: 0,
        lastSyncedAtSecond: 0,
        statusPollIntervalId: null,
        audioUrlRetryTimeoutId: null,
        audioUrlRetryAttempts: 0,
        showRegenerateScriptConfirmDialog: false,
        scriptLocalDirty: false,
        lastSyncedScript: '',
    }),

    computed: {
        ...mapState('Auth', { authUser: state => state.user }),

        isActivityViewRoute() {
            return this.$route?.name === 'activity-view';
        },

        showInstructions() {
            // Preserve existing visibility behavior: instructions show for students (no generate panel)
            // and for instructors/admins once the podcast is ready (or in readOnly contexts).
            return this.isPodcastReady || this.canShowGeneratePodcast;
        },

        isPodcastReady() {
            return this.generationStatus === 'completed' && !!this.audioUrl;
        },
        transcriptScript() {
            const editableScript = typeof this.script === 'string' ? this.script.trim() : '';
            if (editableScript !== '') {
                return this.script;
            }

            return this.podcastScript;
        },
        generationContext: {
            get() {
                return this.activityContent?.generation_context || '';
            },
            set(value) {
                this.$store.dispatch(`${this.namespace}/addActivityContent`, {
                    uuid: this.uuid,
                    field: 'generation_context',
                    value,
                });
            },
        },
        podcastConfig: {
            get() {
                return this.activityContent?.podcast_config || { selections: {} };
            },
            set(value) {
                this.$store.dispatch(`${this.namespace}/addActivityContent`, {
                    uuid: this.uuid,
                    field: 'podcast_config',
                    value,
                });
            },
        },
        script: {
            get() {
                return this.activityContent?.script || '';
            },
            set(value) {
                this.$store.dispatch(`${this.namespace}/addActivityContent`, {
                    uuid: this.uuid,
                    field: 'script',
                    value,
                });
            },
        },
        selectedConfigCount() {
            const selections = this.podcastConfig?.selections || {};
            return Object.values(selections).reduce((count, subcategories) => {
                if (!subcategories || typeof subcategories !== 'object') return count;
                return (
                    count +
                    Object.values(subcategories).reduce((subCount, selected) => {
                        if (Array.isArray(selected)) return subCount + selected.length;
                        if (typeof selected === 'string' && selected !== '') return subCount + 1;
                        return subCount;
                    }, 0)
                );
            }, 0);
        },
        canEditPodcastConfiguration() {
            return this.generationStatus !== 'generating';
        },

        payload() {
            return {
                uuid: this.activity.uuid,
                course_id: this.$route.params.courseId,
                activity_id: this.$route.params.activityId,
                activity_type: this.get('activity_type'),
                activity_submission: {},
            };
        },

        podcastSubmission() {
            return this.userSubmissions[0] || null;
        },

        canShowGeneratePodcast() {
            if (this.readOnly) return false;
            if (!this.course) return false;
            return this.authUser?.is_admin || this.$gate.allows('update-course-info', this.course);
        },

        isTemplateNamespace() {
            return this.namespace === 'ActivityTemplates';
        },
        podcastActivityId() {
            // Prefer the polymorphic content id payload, but fall back to activity contentable id.
            return (
                this.activityContent?.id ||
                this.activityContent?.contentable_id ||
                this.activity?.contentable_id ||
                this.get('contentable_id') ||
                null
            );
        },

        podcastPlaybackActivityId() {
            return this.activity?.id || this.get('id') || this.$route.params.activityId || null;
        },

        podcastStatusUrl() {
            const { podcastActivityId } = this;
            if (!podcastActivityId) return null;

            if (this.isTemplateNamespace) {
                return `/api/podcasts/template/${podcastActivityId}/status`;
            }

            return `/api/podcasts/${podcastActivityId}/status`;
        },

        podcastAudioUrlEndpoint() {
            const { podcastActivityId } = this;
            if (!podcastActivityId) return null;

            if (this.isTemplateNamespace) {
                return `/api/podcasts/template/${podcastActivityId}/audio-url`;
            }

            return `/api/podcasts/${podcastActivityId}/audio-url`;
        },

        showPendingOrGeneratingMessage() {
            return ['pending', 'generating'].includes(this.generationStatus);
        },

        showCompletedWithoutAudioMessage() {
            return this.generationStatus === 'completed' && !this.audioUrl;
        },

        isServiceUnavailableError() {
            const message = (this.generationError || '').toLowerCase();

            return message.includes('contact support') || message.includes('service is not available right now');
        },

        failedGenerationMessage() {
            if (this.isServiceUnavailableError) {
                return 'Contact support, this service is not available right now.';
            }

            return 'The podcast could not be generated yet. Please try generating again.';
        },

        generationStatusLabel() {
            if (this.generationStatus === 'pending') return 'To be generated';
            if (this.generationStatus === 'generating') return 'Generating';
            if (this.generationStatus === 'completed') return 'Generated';
            if (this.generationStatus === 'failed') return 'Generation failed';
            return 'To be generated';
        },
    },

    watch: {
        podcastActivityId(to, from) {
            if (to === from) {
                return;
            }

            this.onPodcastActivityIdentityChanged().catch(() => {});
        },
    },

    async created() {
        this.syncFromActivityContent();
        await this.loadLatestStatus();
        await this.fetchAudioUrlIfAvailable();
        this.scheduleAudioUrlRetryIfNeeded();
        this.ensureStatusPolling();

        // Local-dev fallback: when no audio resolves (no queue worker / TTS provider
        // locally), present a bundled placeholder so the player + transcript render.
        // Production builds drop this branch (import.meta.env.DEV === false).
        if (import.meta.env.DEV && !this.audioUrl) {
            this.generationStatus = 'completed';
            this.audioUrl = DEV_PLACEHOLDER_PODCAST_AUDIO;
            const hasScript = typeof this.transcriptScript === 'string' && this.transcriptScript.trim() !== '';
            if (!hasScript) {
                this.podcastScript = DEV_PLACEHOLDER_PODCAST_TRANSCRIPT;
            }
            this.clearStatusPolling();
            this.clearAudioUrlRetry();
        }
    },

    beforeUnmount() {
        this.clearStatusPolling();
        this.clearAudioUrlRetry();
    },

    methods: {
        // Keyboard activation for the script accordion trigger. Handled explicitly
        // (rather than `@keydown.enter/.space`) because the compiled key-modifier
        // helper (`_withKeys`) is undefined under @vue/compat during a re-render,
        // surfacing as an unhandled rejection in tests. `toggle` is the e-collapse
        // slot-scope function, so it's passed in from the template.
        onScriptTriggerKeydown(event, toggle) {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                toggle();
            }
        },

        async loadLatestStatus() {
            const idWhenStarted = this.podcastActivityId;
            const statusUrl = this.podcastStatusUrl;
            if (!statusUrl) {
                this.syncFromActivityContent();
                return;
            }

            try {
                const { data } = await axios.get(statusUrl);
                if (idWhenStarted !== this.podcastActivityId) {
                    return;
                }
                this.generationStatus = data.generation_status || 'pending';
                this.generationError = data.generation_error || null;
                this.generationStartedAt = data?.generation_started_at ?? null;
                this.generationCompletedAt = data?.generation_completed_at ?? null;
                if (data?.script != null) {
                    this.applyServerScript(data.script);
                }
            } catch (error) {
                if (idWhenStarted === this.podcastActivityId) {
                    this.syncFromActivityContent();
                }
            }
        },

        syncFromActivityContent() {
            const content = this.activityContent || {};
            this.generationStatus = content.generation_status || 'pending';
            this.generationError = content.generation_error || null;
            this.generationStartedAt = content.generation_started_at ?? null;
            this.generationCompletedAt = content.generation_completed_at ?? null;
            this.podcastScript = content.script ?? null;
            this.lastGeneratedScript = content.script ?? null;
            this.lastSyncedScript = content.script ?? '';
            this.scriptLocalDirty = false;
        },

        async generatePodcast() {
            const { podcastActivityId } = this;
            if (!podcastActivityId) return;

            const hasScriptChanges =
                this.generationStatus === 'completed' &&
                typeof this.script === 'string' &&
                this.script.trim() !== '' &&
                this.script.trim() !== (this.lastGeneratedScript || '');

            if (hasScriptChanges) {
                this.showRegenerateScriptConfirmDialog = true;
                return;
            }

            await this.queuePodcastGeneration();
        },

        async queuePodcastGeneration() {
            this.showRegenerateScriptConfirmDialog = false;
            const { podcastActivityId } = this;
            if (!podcastActivityId) return;

            const idAtQueueStart = podcastActivityId;

            this.isGenerating = true;
            this.generationError = null;
            this.clearAudioUrlRetry();
            this.audioUrl = null;

            const previousGenerationStatus = this.generationStatus;
            const previousGenerationStartedAt = this.generationStartedAt;
            const previousGenerationCompletedAt = this.generationCompletedAt;
            this.generationStatus = 'pending';
            this.generationStartedAt = null;
            this.generationCompletedAt = null;

            try {
                const { data } = await axios.post(
                    `/api/podcasts/${podcastActivityId}/generate`,
                    this.buildGenerationPayload()
                );
                if (idAtQueueStart !== this.podcastActivityId) {
                    this.syncFromActivityContent();
                    return;
                }
                this.generationStatus = data?.generation_status || 'pending';
                this.generationStartedAt = data?.generation_started_at ?? null;
                this.generationCompletedAt = data?.generation_completed_at ?? null;
                this.generationError = data?.generation_error || null;
                this.ensureStatusPolling(true);
                await this.pollStatus();
            } catch (error) {
                if (idAtQueueStart === this.podcastActivityId) {
                    this.generationStatus = previousGenerationStatus;
                    this.generationStartedAt = previousGenerationStartedAt;
                    this.generationCompletedAt = previousGenerationCompletedAt;
                    this.generationError = error?.response?.data?.message || 'Unable to queue podcast generation.';
                }
            } finally {
                this.isGenerating = false;
            }
        },

        ensureStatusPolling(forceRestart = false) {
            const shouldPoll =
                ['pending', 'generating'].includes(this.generationStatus) ||
                (this.generationStatus === 'completed' && !this.audioUrl);
            if (!shouldPoll) {
                this.clearStatusPolling();
                return;
            }

            if (this.statusPollIntervalId && !forceRestart) {
                return;
            }

            this.clearStatusPolling();
            this.statusPollIntervalId = setInterval(() => {
                this.pollStatus();
            }, 15000);
        },

        clearStatusPolling() {
            if (this.statusPollIntervalId) {
                clearInterval(this.statusPollIntervalId);
                this.statusPollIntervalId = null;
            }
        },

        clearAudioUrlRetry() {
            if (this.audioUrlRetryTimeoutId) {
                clearTimeout(this.audioUrlRetryTimeoutId);
                this.audioUrlRetryTimeoutId = null;
            }
        },

        scheduleAudioUrlRetryIfNeeded() {
            const shouldRetry = this.generationStatus === 'completed' && !this.audioUrl;
            if (!shouldRetry) {
                this.clearAudioUrlRetry();
                this.audioUrlRetryAttempts = 0;
                return;
            }

            if (this.audioUrlRetryTimeoutId || this.audioUrlRetryAttempts >= 10) {
                return;
            }

            this.audioUrlRetryAttempts += 1;
            this.audioUrlRetryTimeoutId = setTimeout(async () => {
                this.audioUrlRetryTimeoutId = null;
                await this.fetchAudioUrlIfAvailable();
                this.scheduleAudioUrlRetryIfNeeded();
            }, 3000);
        },

        async pollStatus() {
            const idWhenStarted = this.podcastActivityId;
            const statusUrl = this.podcastStatusUrl;
            if (!statusUrl) return;

            try {
                const { data } = await axios.get(statusUrl);
                if (idWhenStarted !== this.podcastActivityId) {
                    return;
                }
                this.generationStatus = data.generation_status || 'pending';
                this.generationError = data.generation_error || null;
                this.generationStartedAt = data?.generation_started_at ?? null;
                this.generationCompletedAt = data?.generation_completed_at ?? null;
                if (data?.script != null) {
                    this.applyServerScript(data.script);
                }

                if (this.generationStatus === 'completed') {
                    await this.fetchAudioUrlIfAvailable();

                    if (this.audioUrl) {
                        this.clearStatusPolling();
                    } else {
                        this.scheduleAudioUrlRetryIfNeeded();
                    }
                }

                if (this.generationStatus === 'failed') {
                    this.clearStatusPolling();
                    this.clearAudioUrlRetry();
                }
            } catch (error) {
                // Keep polling on transient issues and retry URL retrieval if generation is complete.
                if (idWhenStarted !== this.podcastActivityId) {
                    return;
                }
                if (this.generationStatus === 'completed' && !this.audioUrl) {
                    await this.fetchAudioUrlIfAvailable();
                    this.scheduleAudioUrlRetryIfNeeded();
                }
            }
        },

        async fetchAudioUrlIfAvailable() {
            if (!['completed'].includes(this.generationStatus)) return;
            const idWhenStarted = this.podcastActivityId;
            const audioUrlEndpoint = this.podcastAudioUrlEndpoint;
            if (!audioUrlEndpoint) return;

            try {
                const { data } = await axios.get(audioUrlEndpoint);
                if (idWhenStarted !== this.podcastActivityId) {
                    return;
                }
                this.audioUrl = data?.url || null;
            } catch (error) {
                if (idWhenStarted === this.podcastActivityId) {
                    this.audioUrl = null;
                }
            }

            if (idWhenStarted !== this.podcastActivityId) {
                return;
            }

            if (this.audioUrl) {
                this.clearAudioUrlRetry();
                this.audioUrlRetryAttempts = 0;
                this.clearStatusPolling();
            }

            if (this.podcastSubmission?.submittable) {
                this.listenPercentage = this.podcastSubmission.submittable.listen_percentage || 0;
                this.lastPosition = this.podcastSubmission.submittable.last_position_seconds || 0;
                this.totalListenTimeSeconds = this.podcastSubmission.submittable.total_listen_time_seconds || 0;
            }
        },

        async onPodcastActivityIdentityChanged() {
            this.clearStatusPolling();
            this.clearAudioUrlRetry();
            this.audioUrlRetryAttempts = 0;
            this.isGenerating = false;
            this.showRegenerateScriptConfirmDialog = false;
            this.audioUrl = null;
            this.listenPercentage = 0;
            this.lastPosition = 0;
            this.totalListenTimeSeconds = 0;
            this.lastSyncedAtSecond = 0;
            this.syncFromActivityContent();
            await this.loadLatestStatus();
            await this.fetchAudioUrlIfAvailable();
            this.scheduleAudioUrlRetryIfNeeded();
            this.ensureStatusPolling();
        },

        async onTimeUpdate({ currentTime, duration }) {
            if (!duration) return;

            const previousLastPosition = this.lastPosition;
            this.lastPosition = Math.floor(currentTime);
            this.listenPercentage = Math.min(100, Math.floor((currentTime / duration) * 100));

            const forwardDelta = this.lastPosition - previousLastPosition;
            if (forwardDelta > 0) {
                this.totalListenTimeSeconds += forwardDelta;
            }

            if (this.lastPosition - this.lastSyncedAtSecond < 5) {
                return;
            }

            this.lastSyncedAtSecond = this.lastPosition;
            await this.syncPlaybackProgress();
        },

        async onEnded() {
            this.listenPercentage = 100;
            await this.syncPlaybackProgress();
        },

        async syncPlaybackProgress() {
            const activityId = this.podcastPlaybackActivityId;
            if (!activityId) return;

            try {
                const { data } = await axios.put(`/api/podcasts/${activityId}/playback`, {
                    last_position_seconds: this.lastPosition,
                    furthest_position_seconds: this.lastPosition,
                    listen_percentage: this.listenPercentage,
                    total_listen_time_seconds: this.totalListenTimeSeconds,
                });

                if (data?.data?.marked_completed_at) {
                    this.$store.commit(`Activities/${this.uuid}/EDIT_FIELD`, {
                        field: 'completed',
                        value: data.data.marked_completed_at,
                    });
                }
            } catch (error) {
                // Keep the user flow uninterrupted if telemetry sync fails.
            }
        },
        buildGenerationPayload() {
            return {
                podcast_config: JSON.parse(JSON.stringify(this.podcastConfig || { selections: {} })),
                generation_context: this.generationContext || '',
                script: this.script || '',
            };
        },
        handleScriptInput(value) {
            const nextValue = typeof value === 'string' ? value : '';
            this.scriptLocalDirty = nextValue !== this.lastSyncedScript;
            this.script = nextValue;
        },
        shouldApplyServerScript(serverScript) {
            if (!this.scriptLocalDirty) {
                return true;
            }

            const currentScript = typeof this.script === 'string' ? this.script : '';

            return this.lastSyncedScript === serverScript && currentScript === this.lastSyncedScript;
        },
        applyServerScript(serverScript) {
            this.podcastScript = serverScript;
            this.lastGeneratedScript = serverScript;

            if (!this.shouldApplyServerScript(serverScript)) {
                return;
            }

            this.script = serverScript;
            this.lastSyncedScript = serverScript;
            this.scriptLocalDirty = false;
        },
        handleConfigApply(payload) {
            if (!payload) return;
            this.podcastConfig = payload.config;
            this.generationContext = payload.text;
        },
    },
};
</script>

<style scoped>
.podcast-player-wrap {
    width: 100%;
    max-width: 700px;
}

.podcast-config-panel {
    width: 100%;
    max-width: 50vw;
}

/* On mobile the 50vw cap leaves the config card and the delivery/script
   editors at roughly half width, which looks off. Let them fill the column. */
@media (max-width: 768px) {
    .podcast-config-panel {
        max-width: 100%;
    }
}

.podcast-script-editor :deep(textarea) {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
}

.podcast-script-block :deep(.e-collapse-accordion) {
    width: 100%;
    max-width: 450px;
    min-width: 0;
    --p-accordion-header-padding: 0;
    --p-accordion-content-padding: 0;
}

.podcast-script-trigger {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.625rem 0.75rem;
    cursor: pointer;
    border: 1px solid var(--p-content-border-color, #e5e7eb);
    border-radius: 0.375rem;
    background: var(--p-content-surface-ground, #f9fafb);
    transition: background-color 0.15s ease;
}

.podcast-script-trigger:hover {
    background: var(--p-content-surface-hover, #f3f4f6);
}

.podcast-script-trigger:focus-visible {
    outline: 2px solid var(--p-focus-ring-color, #3b82f6);
    outline-offset: 2px;
}

.podcast-script-content {
    width: 100%;
    max-width: 100%;
    margin-top: 0.5rem;
    padding: 1rem 1.25rem;
    font-size: 0.875rem;
    line-height: 1.6;
    white-space: pre-wrap;
    word-break: break-word;
    overflow-wrap: anywhere;
    border: 1px solid var(--p-content-border-color, #e5e7eb);
    border-radius: 0.375rem;
    background: #fff;
    max-height: 20rem;
    overflow-y: auto;
}
</style>
